import { useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import {
  confirmMemberCsv,
  CSV_ACTION_LABELS,
  memberExportUrl,
  uploadMemberCsv,
  type CsvJob,
  type CsvRowResult, csvSampleUrl } from '@/features/csv/api';


const COLUMNS: Column<CsvRowResult>[] = [
  { key: 'row_no', header: '行', render: (row) => row.row_no },
  { key: 'member_id', header: '会員ID', primary: true, render: (row) => row.member_id ?? '—' },
  { key: 'action', header: '判定', render: (row) => CSV_ACTION_LABELS[row.action] },
  {
    key: 'errors',
    header: 'エラー・警告',
    render: (row) =>
      [...row.errors, ...row.warnings].length > 0 ? (
        <ul className="list-inside list-disc">
          {row.errors.map((e) => (
            <li key={e} className="text-wpnotice-error">
              {e}
            </li>
          ))}
          {row.warnings.map((w) => (
            <li key={w} className="text-wpnotice-warning">
              {w}
            </li>
          ))}
        </ul>
      ) : (
        '—'
      ),
  },
];

/**
 * 会員CSV一括登録・更新（指示書 §8・§12 M3 / DECISIONS D-012）。
 * 3段階API: アップロード（検証+プレビュー）→ 理由入力の上で確定 → 結果表示。
 */
export function MemberCsvImportPage(): ReactElement {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [job, setJob] = useState<CsvJob | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onFileSelected(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    setJob(null);
    try {
      setJob(await uploadMemberCsv(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(): Promise<void> {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const confirmed = await confirmMemberCsv(job.id, reason);
      setJob(confirmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : '確定に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const hasErrors = (job?.summary?.error_count ?? 0) > 0;
  const isConfirmed = job?.status === 'confirmed';

  return (
    <>
      <PageHeader
        title="会員CSV取込"
        description="Excelなどで作った一覧から、会員をまとめて登録・更新します。ファイルを選んだ時点では登録されません（内容を確認してから「確定」します）。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {isConfirmed && (
        <Notice kind="success" dismissible={false}>
          確定しました（新規 {job.summary?.create_count ?? 0} 件 / 更新{' '}
          {job.summary?.update_count ?? 0} 件）。
        </Notice>
      )}

      <MetaBox title="1. ファイルを選択">
        <p className="mb-2 text-wptext-muted">
          はじめての場合は、
          <a href={memberExportUrl()} className="text-wpaccent hover:underline">
            現在の会員をCSV出力
          </a>
          して、そのファイルを書き換えて取り込むのが確実です（列の並びがそのまま使えます）。
          まだ会員がいない場合は
          <a href={csvSampleUrl('member')} className="text-wpaccent hover:underline">
            サンプルCSVをダウンロード
          </a>
          して、記入例を置き換えてください。
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFileSelected(file);
          }}
        />
        <details className="mt-2">
          <summary className="cursor-pointer text-wpaccent">列の説明を見る</summary>
          <ul className="mt-1 list-inside list-disc text-xs text-wptext-muted">
            <li>
              会員No. … <strong>必須</strong>。同じ会員No.の行があれば更新、なければ新規登録になります
            </li>
            <li>
              氏名 … 必須。ふりがな・企業名・役職・電話番号・連絡先メールは任意
            </li>
            <li>
              期コード／コースコード … 新規登録では<strong>両方必須</strong>。更新では空欄なら今の所属のままです。
              業務マスターに登録済みのコードを書きます
            </li>
            <li>
              在籍状態 … <code>active</code>（在籍）／<code>graduated</code>（卒業）／
              <code>withdrawn</code>（退会）
            </li>
            <li>
              タグ … 複数付けるときは<strong>セミコロン（;）</strong>で区切ります（例: 製造業;関西）
            </li>
            <li>空欄にした項目は変更されません（消したい場合は事務局側の編集画面で消してください）</li>
          </ul>
        </details>
      </MetaBox>

      {job && (
        <div className="mt-4">
          <MetaBox title="2. プレビュー">
            <dl className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-wptext-muted">
              <span>
                新規 <strong className="text-wptext-strong">{job.summary?.create_count ?? 0}</strong>
              </span>
              <span>
                更新 <strong className="text-wptext-strong">{job.summary?.update_count ?? 0}</strong>
              </span>
              <span>
                変更なし{' '}
                <strong className="text-wptext-strong">{job.summary?.no_change_count ?? 0}</strong>
              </span>
              <span>
                エラー{' '}
                <strong className="text-wpnotice-error">{job.summary?.error_count ?? 0}</strong>
              </span>
            </dl>
            <ListTable<CsvRowResult>
              caption="CSVプレビュー"
              rows={job.rows}
              columns={COLUMNS}
              rowKey={(row) => String(row.row_no)}
            />
          </MetaBox>

          {!isConfirmed && (
            <MetaBox title="3. 確定">
              {hasErrors ? (
                <p className="text-wpnotice-error">
                  エラーが残っています。ファイルを修正して再アップロードしてください。
                </p>
              ) : (
                <>
                  <label htmlFor="csv-reason" className="mb-1.5 block text-wptext-muted">
                    確定理由（あとから「誰が何のために取り込んだか」を追えるように残します）
                  </label>
                  <textarea
                    id="csv-reason"
                    required
                    rows={2}
                    placeholder="例: 第4期の新規入会者20名を登録"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mb-3 w-full rounded-sm border border-wpborder px-3 py-2"
                  />
                  <button
                    type="button"
                    disabled={busy || !reason.trim()}
                    onClick={() => void onConfirm()}
                    className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                  >
                    確定する
                  </button>
                </>
              )}
            </MetaBox>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/members')}
        className="mt-4 text-wpaccent hover:underline"
      >
        ← 会員一覧に戻る
      </button>
    </>
  );
}
